import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BellRing, CheckCircle2, MailQuestion, Phone } from "lucide-react";
import { SectionTag } from "@/components/Motion";
import PageSeo from "@/components/PageSeo";
import { COMPANY, telHref } from "@/data/company";
import { openCallbackModal, trackEvent } from "@/lib/analytics";
import { postToApi, submitErrorMessage } from "@/lib/api";
import useSlowHint, { SLOW_HINT } from "@/lib/useSlowHint";

const SUBJECTS = [
  "Question sur un devis décennale",
  "Santé dirigeants TNS",
  "Santé des salariés",
  "Prévoyance TNS",
  "Partenariat",
  "Autre demande",
];

const inputCls =
  "w-full rounded-none border-b-2 border-ink/40 bg-transparent py-3 text-base outline-none transition-colors placeholder:text-ink/30 focus:border-accent";

export default function Contact() {
  const [form, setForm] = useState({ nom: "", email: "", sujet: "", message: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [formError, setFormError] = useState("");
  const slow = useSlowHint(loading);

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const submit = async () => {
    const e = {};
    if (form.nom.trim().length < 2) e.nom = "Nom requis";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "E-mail invalide";
    if (!form.sujet) e.sujet = "Sélectionnez un sujet";
    if (form.message.trim().length < 10) e.message = "Message trop court (10 caractères min.)";
    setErrors(e);
    if (Object.keys(e).length > 0) {
      setFormError("Certains champs sont incomplets : vérifiez les messages ci-dessus.");
      return;
    }
    setFormError("");
    setLoading(true);
    try {
      await postToApi("/contact", {
        nom: form.nom.trim(),
        email: form.email.trim(),
        sujet: form.sujet,
        message: form.message.trim(),
      });
      trackEvent("contact_completed", { sujet: form.sujet });
      setSent(true);
      window.scrollTo({ top: 0 });
    } catch (err) {
      trackEvent("contact_error", { error_type: err.code || err.message || "unknown" });
      setFormError(submitErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageSeo path="/contact" />
      <div className="mx-auto grid max-w-[1440px] lg:grid-cols-12">
        <aside className="border-b lg:border-b-0 lg:border-r border-ink/15 px-5 sm:px-8 py-10 lg:py-16 lg:col-span-5">
          <SectionTag className="mb-4">Contact</SectionTag>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-[min(3.6vw,3.75rem)] font-extrabold uppercase tracking-tighter leading-[0.95] mb-8">
            Une question ?<br /><span className="text-accent">Écrivez-nous.</span>
          </h1>
          <div className="border-2 border-ink bg-white p-6 shadow-brutal">
            <MailQuestion className="h-6 w-6 text-accent mb-4" />
            <p className="text-sm leading-relaxed text-ink/75">
              Écrivez-nous avec ce formulaire : nous vous répondons par écrit rapidement —
              objectif : sous 24h ouvrées. Vous préférez un échange de vive voix ?
              Demandez à être rappelé sur le créneau de votre choix.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={openCallbackModal}
                className="inline-flex items-center gap-2 border-2 border-ink px-5 py-3 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors hover:bg-ink hover:text-paper"
              >
                <BellRing className="h-4 w-4" />
                Être rappelé
              </button>
              {COMPANY.phone && (
                <a
                  href={telHref(COMPANY.phone)}
                  onClick={() => trackEvent("phone_click", { position: "contact" })}
                  className="inline-flex items-center gap-2 border-2 border-ink bg-ink px-5 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-paper"
                >
                  <Phone className="h-4 w-4" />
                  {COMPANY.phone}
                </a>
              )}
            </div>
          </div>
          <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-ink/60 leading-relaxed">
            Pour un devis RC Pro + décennale, privilégiez le{" "}
            <Link to="/devis" className="text-accent underline underline-offset-4">
              formulaire de devis
            </Link>{" "}
            — plus rapide et plus précis.
          </p>
        </aside>

        <div className="lg:col-span-7 px-5 sm:px-8 py-10 lg:py-16">
          {sent ? (
              <div
                data-testid="contact-success"
                className="border-2 border-ink bg-white p-8 sm:p-12 shadow-brutal-accent animate-in fade-in slide-in-from-bottom-4 duration-500"
              >
                <CheckCircle2 className="h-12 w-12 text-accent mb-6" />
                <h2 className="font-display text-3xl font-extrabold uppercase tracking-tighter mb-4">
                  Message envoyé.
                </h2>
                <p className="text-base leading-relaxed text-ink/75">
                  Merci {form.nom}. Votre message a bien été transmis à notre équipe — nous vous
                  répondrons à l'adresse <strong>{form.email}</strong> — objectif : sous 24h ouvrées.
                </p>
              </div>
            ) : (
              <div>
                <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tighter mb-8">
                  Formulaire de contact
                </h2>
                <div className="grid gap-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-nom" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-ink/60">
                      Nom <span className="text-accent">*</span>
                    </label>
                    <input
                      id="contact-nom" data-testid="contact-nom"
                      type="text"
                      className={inputCls}
                      placeholder="Votre nom"
                      value={form.nom}
                      onChange={(e) => set("nom", e.target.value)}
                    />
                    {errors.nom && <p className="mt-1.5 font-mono text-[11px] uppercase text-accent">{errors.nom}</p>}
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-ink/60">
                      E-mail <span className="text-accent">*</span>
                    </label>
                    <input
                      id="contact-email" data-testid="contact-email"
                      type="email"
                      className={inputCls}
                      placeholder="vous@exemple.fr"
                      value={form.email}
                      onChange={(e) => set("email", e.target.value)}
                    />
                    {errors.email && <p className="mt-1.5 font-mono text-[11px] uppercase text-accent">{errors.email}</p>}
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="contact-sujet" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-ink/60">
                      Sujet <span className="text-accent">*</span>
                    </label>
                    <select
                      id="contact-sujet" data-testid="contact-sujet"
                      className={`${inputCls} cursor-pointer`}
                      value={form.sujet}
                      onChange={(e) => set("sujet", e.target.value)}
                    >
                      <option value="">Sélectionnez un sujet</option>
                      {SUBJECTS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    {errors.sujet && <p className="mt-1.5 font-mono text-[11px] uppercase text-accent">{errors.sujet}</p>}
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="contact-message" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-ink/60">
                      Message <span className="text-accent">*</span>
                    </label>
                    <textarea
                      id="contact-message" data-testid="contact-message"
                      rows={6}
                      className={`${inputCls} resize-y border-2 border-ink/40 p-4 focus:border-accent`}
                      placeholder="Décrivez votre demande…"
                      value={form.message}
                      onChange={(e) => set("message", e.target.value)}
                    />
                    {errors.message && <p className="mt-1.5 font-mono text-[11px] uppercase text-accent">{errors.message}</p>}
                  </div>
                </div>
                <button
                  data-testid="contact-submit"
                  onClick={submit}
                  disabled={loading}
                  className="mt-12 inline-flex items-center gap-3 border-2 border-ink bg-accent px-10 py-4 font-mono text-xs uppercase tracking-[0.2em] text-paper shadow-brutal transition-all hover:bg-ink hover:shadow-none disabled:opacity-60"
                >
                  {loading ? "Envoi en cours…" : "Envoyer le message"}
                  <ArrowRight className="h-4 w-4" />
                </button>
                {(formError || slow) && (
                  <p role="alert" className="mt-4 max-w-xl text-sm leading-relaxed text-accent" data-testid="contact-error">
                    {formError || SLOW_HINT}
                  </p>
                )}
              </div>
            )}
        </div>
      </div>
    </div>
  );
}
